import { isAxiosError } from 'axios';
import { merge } from 'es-toolkit';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';

import {
  getJobFrameworkRouteJobFrameworkSessionIdGet,
  updateJobFrameworkRouteJobFrameworkSessionIdPut,
} from '@/services/python-api';

import type { JobFramework } from '../types/chat';

const jobFramework = ref<JobFramework | null>(null);
const isLoadingFramework = ref(false);
const isLoadingFrameworkFromAssistant = ref(false);

export const useJobData = () => {
  const { t } = useI18n();

  const canDownload = computed(() => {
    return jobFramework.value?.families?.some((family) => family.name.trim() !== '') ?? false;
  });

  let updateFrameworkController: AbortController | null = null;

  // Unified fetch function for job framework
  const fetchJobFramework = async (sessionId: string, fromAssistant = false) => {
    if (!sessionId) return;

    // Loader only for first load
    if (!jobFramework.value) {
      isLoadingFramework.value = true;
    }

    if (fromAssistant) {
      isLoadingFrameworkFromAssistant.value = true;
    }

    try {
      const { data } = await getJobFrameworkRouteJobFrameworkSessionIdGet({
        throwOnError: true,
        path: { session_id: sessionId },
      });

      jobFramework.value = data as JobFramework | null;
    } catch (err) {
      if (isAxiosError(err) && err?.response?.status === 404) {
        jobFramework.value = null;
        return;
      }

      toast.error(t('chat.errors.fetch_failed'));
      throw err; // Sentry
    } finally {
      isLoadingFramework.value = false;
      isLoadingFrameworkFromAssistant.value = false;
    }
  };

  // Function to update job framework
  const updateJobFramework = async (sessionId: string, framework: JobFramework) => {
    if (!sessionId || !framework) return;

    updateFrameworkController?.abort();
    updateFrameworkController = new AbortController();

    try {
      const { data } = await updateJobFrameworkRouteJobFrameworkSessionIdPut({
        throwOnError: true,
        path: { session_id: sessionId },
        body: framework,
        signal: updateFrameworkController.signal,
      });

      // Update scores but not the families to prevent infinite loop
      jobFramework.value = merge(jobFramework.value ?? {}, {
        quality_score: data.quality_score,
        completeness_score: data.completeness_score,
      }) as JobFramework;
    } catch (err) {
      if (isAxiosError(err) && err.code === 'ERR_CANCELED') {
        return;
      }

      toast.error(t('chat.errors.fetch_failed'));
      throw err; // Sentry
    }
  };

  // Debounced auto-save function for seamless persistence
  let saveTimeout: NodeJS.Timeout | null = null;
  const autoSaveJobFramework = (sessionId: string, framework: JobFramework) => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    saveTimeout = setTimeout(async () => {
      try {
        await updateJobFramework(sessionId, framework);
      } catch (error) {
        toast.error(t('chat.errors.save_failed'));
        throw error; // Sentry
      }
    }, 1000); // Save after 1 second of inactivity
  };

  // Function to clear all data (for new sessions)
  const clearJobData = () => {
    jobFramework.value = null;
    isLoadingFramework.value = false;
    isLoadingFrameworkFromAssistant.value = false;
  };

  const familiesCount = computed(() => {
    return jobFramework.value?.families?.length ?? 0;
  });

  const subFamiliesCount = computed(() => {
    return jobFramework.value?.families?.reduce((acc, family) => acc + (family.sub_families?.length ?? 0), 0) ?? 0;
  });

  const jobDescriptionsCount = computed(() => {
    return jobFramework.value?.families?.reduce(
      (acc, family) =>
        acc + (family.sub_families?.reduce((acc, subFamily) => acc + (subFamily.job_profiles?.length ?? 0), 0) ?? 0),
      0,
    );
  });

  return {
    jobFramework,
    isLoadingFramework,
    isLoadingFrameworkFromAssistant,
    fetchJobFramework,
    updateJobFramework,
    autoSaveJobFramework,
    canDownload,
    clearJobData,
    familiesCount,
    subFamiliesCount,
    jobDescriptionsCount,
  };
};
